import React, { Component, Fragment } from 'react';

import { HotmartRum } from '@hot-observability-js/react';
import { Loader } from 'components/loader';
import { fabric } from 'fabric';
import CanvasHelper from 'helpers/Canvas';
import { setThumborSignedURL } from 'helpers/ImageFile';
import get from 'lodash/get';
import { inject, PropTypes as MobxPropTypes } from 'mobx-react';
import ConfigService from 'modules/configManager/ConfigService';
import PropTypes from 'prop-types';

import CanvasObjectDelete from './CanvasObjectDelete';
import {
  CANVAS_FLOAT_PRECISION,
  CERTIFICATE_EDITOR_AREA_PROPORTION
} from './constants';
import CanvasMenu from './menu/CanvasMenu';
import PreviewCertificate from './PreviewCertificate';
import ResetCertificate from './ResetCertificate';

import './CanvasEditor.scss';

@inject((stores) => ({
  storeCertificate: stores.ConfigStore.certificate
}))
export default class CanvasEditor extends Component {
  static propTypes = {
    storeCertificate: MobxPropTypes.objectOrObservableObject,
    canvasData: PropTypes.object,
    width: PropTypes.number,
    height: PropTypes.number,
    isDefault: PropTypes.bool,
    openCertificate: PropTypes.func
  };

  static defaultProps = {
    isDefault: false
  };

  state = {
    loading: false,
    defaultTemplate: {}
  };

  canvasRef = React.createRef();
  canvasEditorRef = React.createRef();

  constructor(props) {
    super(props);

    this.resetCanvas = this.resetCanvas.bind(this);
  }

  async componentDidMount() {
    this.setState({ loading: true });

    const defaultCertificate = await ConfigService.fetchDefaultCertificate();

    await this.declareCanvas();

    this.setState({
      loading: false,
      defaultTemplate: get(defaultCertificate, 'templateJson')
    });
  }

  async componentDidUpdate() {
    const { canvasData } = this.state;

    if (this.props.canvasData !== canvasData) {
      const { canvasData } = this.props;

      await this.fillCanvas(canvasData);
      this.setState({ canvasData });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const loadingChange = this.loadingHaveBeenModificated(
      this.state.loading,
      nextState.loading
    );
    const canvasChange = this.canvasHaveBeenModificated(this.props.canvasData);

    return loadingChange || canvasChange;
  }

  loadingHaveBeenModificated(actualCanvasLoading, nextLoading) {
    return actualCanvasLoading !== nextLoading;
  }

  canvasHaveBeenModificated(actualCanvasData = {}, newCanvasData = {}) {
    const { backgroundImage: actualBackground } = actualCanvasData;
    const { backgroundImage: newBackground } = newCanvasData;

    return actualBackground !== newBackground;
  }

  handleObjectModified = () => {
    const { hasChangeOnTemplate } = this.props.storeCertificate;

    if (!hasChangeOnTemplate) {
      ConfigService.setCertificateData({ hasChangeOnTemplate: true });
    }
  };

  async declareCanvas() {
    const { width, height } = this.defineCanvasDimensions();
    const { canvasData } = this.state;

    fabric.Object.NUM_FRACTION_DIGITS = CANVAS_FLOAT_PRECISION;

    const canvas = new fabric.Canvas(this.canvasRef?.current, {
      width,
      height
    });

    const boundDefineScaleBoundaries = CanvasHelper.defineScaleBoundaries.bind(
      CanvasHelper,
      canvas,
      this.showDeleteButton
    );

    const boundDefineCanvasBoundaries =
      CanvasHelper.defineCanvasBoundaries.bind(
        CanvasHelper,
        canvas,
        this.showDeleteButton
      );

    const boundSelectObject = CanvasHelper.selectObject.bind(
      CanvasHelper,
      canvas,
      this.showDeleteButton
    );

    const boundResetCursor = CanvasHelper.resetCursor.bind(
      CanvasHelper,
      canvas
    );

    canvas.on({
      'object:scaling': boundDefineScaleBoundaries,
      'object:moving': boundDefineCanvasBoundaries,
      'object:rotating': boundDefineCanvasBoundaries,
      'object:modified': () => this.handleObjectModified(),
      'selection:created': boundSelectObject,
      'selection:updated': boundSelectObject,
      'selection:cleared': ConfigService.hideDeleteButton,
      'mouse:down': boundResetCursor
    });

    ConfigService.setCertificateData({ canvas });
    await this.fillCanvas(canvasData);
  }

  async fillCanvas(canvasData) {
    const { canvas } = this.props.storeCertificate;
    const { isDefault } = this.props;

    if (!canvas) {
      return;
    }

    canvas.clear();

    if (canvasData) {
      await CanvasHelper.loadCanvasData(canvas, {
        ...canvasData,
        backgroundImage: {}
      });
      isDefault && CanvasHelper.alignObjectsInCenter(canvas);
      canvasData.backgroundImage &&
        (await this.loadBackgroundFromURL(
          canvasData.backgroundImage.src,
          canvas
        ));
    }
  }

  loadBackgroundFromURL(src, canvas) {
    return new Promise((resolve, reject) => {
      if (!src) {
        reject();
      }

      const thumborSignedSrc = setThumborSignedURL(src);

      fabric.Image.fromURL(thumborSignedSrc, (img, error) => {
        const backgroundConfig = CanvasHelper.getBackgroundConfigToFitCanvas(
          img,
          canvas
        );

        if (error || !CanvasHelper.backgroundConfigIsValid(backgroundConfig)) {
          HotmartRum.captureMessage(
            'Error loading background image',
            (scope) => {
              scope.setContext('Certificate', {
                backgroundConfig,
                error,
                url: thumborSignedSrc
              });
            }
          );
        }

        canvas.setBackgroundImage(
          img,
          canvas.renderAll.bind(canvas),
          backgroundConfig
        );

        resolve(canvas);
      });
    });
  }

  defineCanvasDimensions() {
    let { width, height } = this.props;

    const editorDiv = this.canvasEditorRef?.current;

    if (editorDiv) {
      width = width || editorDiv.offsetWidth;
      height = height || width / CERTIFICATE_EDITOR_AREA_PROPORTION;
    }

    return { width, height };
  }

  showDeleteButton(button) {
    ConfigService.setDeleteButtonPosition(button);
  }

  async resetCanvas() {
    const { defaultTemplate } = this.state;
    const { canvas } = this.props.storeCertificate;

    ConfigService.setCertificateData({ hasChangeOnTemplate: false });

    await this.fillCanvas(defaultTemplate);
    CanvasHelper.alignObjectsInCenter(canvas);
  }

  setLoader = (value = false) => {
    const { loading } = this.state;

    if (loading !== value) {
      this.setState({ loading: value });
    }
  };

  renderLoading() {
    return <Loader key="loader" mask className="canvas-editor__loader" />;
  }

  renderEditor() {
    const { canvas } = this.props.storeCertificate;
    const { openCertificate } = this.props;

    return (
      <Fragment>
        <div ref={this.canvasEditorRef} className="canvas-editor__area">
          <CanvasObjectDelete canvas={canvas} />
          <CanvasMenu canvasRef={canvas} setLoader={this.setLoader} />
          <canvas ref={this.canvasRef} />
        </div>
        <div className="canvas-options__area _mt-6">
          <PreviewCertificate openCertificate={openCertificate} />
          <ResetCertificate resetCanvas={this.resetCanvas} />
        </div>
      </Fragment>
    );
  }

  render() {
    const { loading } = this.state;

    return (
      <div>
        {loading && this.renderLoading()}
        {this.renderEditor()}
      </div>
    );
  }
}
